import type { FC } from "react";
import { createContext } from "react";
import type { Integration } from "../portals/messagemedia/hooks/useIntegration";
import type { IntegrationConnectionState } from "../portals/messagemedia/hooks/useValidateConnection";
import {
  isIntegrationActiveFromContext,
  getAllIntegrationsFromContext,
  getAllConnectionStatesFromContext,
} from "./integrationUtils";

class IntegrationStateManager {
  private state: IntegrationsState | null = null;

  setState(state: IntegrationsState) {
    this.state = state;
  }

  isIntegrationActive(
    integrationName: keyof IntegrationsState["integrations"]
  ): boolean {
    if (!this.state) return false;
    return isIntegrationActiveFromContext(
      this.state.integrations,
      integrationName
    );
  }

  getAllIntegrations(): Integration[] {
    if (!this.state) return [];
    return getAllIntegrationsFromContext(this.state.integrations);
  }

  getAllConnectionStates(): IntegrationConnectionState[] {
    if (!this.state) return [];
    return getAllConnectionStatesFromContext(this.state.connectionStates);
  }
}

export const integrationStateManager = new IntegrationStateManager();

export type IntegrationsState = {
  integrations: {
    bigcommerce: Integration | null;
    shopify: Integration | null;
    woocommerce: Integration | null;
    mailjet: Integration | null;
  };
  integrationsLoading: {
    bigcommerce: boolean;
    shopify: boolean;
    woocommerce: boolean;
    mailjet: boolean;
  };
  connectionStates: {
    shopify: IntegrationConnectionState | null;
  };
};

const IntegrationsContext = createContext<{
  state: IntegrationsState;
  getIntegrationState: (
    integrationName: keyof IntegrationsState["integrations"]
  ) => Integration | null;
  isIntegrationActive: (
    integrationName: keyof IntegrationsState["integrations"]
  ) => boolean;
  getAllIntegrations: () => Integration[];
  getAllConnectionStates: () => IntegrationConnectionState[];
} | null>(null);

export const IntegrationsContextProvider: FC<{
  children: React.ReactNode;
  integrationsState: IntegrationsState;
}> = ({ children, integrationsState }) => {
  const getIntegrationState = (
    integrationName: keyof IntegrationsState["integrations"]
  ): Integration | null => integrationsState.integrations[integrationName];

  const isIntegrationActive = (
    integrationName: keyof IntegrationsState["integrations"]
  ): boolean =>
    isIntegrationActiveFromContext(
      integrationsState.integrations,
      integrationName
    );

  const getAllIntegrations = (): Integration[] =>
    getAllIntegrationsFromContext(integrationsState.integrations);

  const getAllConnectionStates = (): IntegrationConnectionState[] =>
    getAllConnectionStatesFromContext(integrationsState.connectionStates);

  // Sync state with global manager
  integrationStateManager.setState(integrationsState);

  return (
    <IntegrationsContext.Provider
      value={{
        state: integrationsState,
        getIntegrationState,
        isIntegrationActive,
        getAllIntegrations,
        getAllConnectionStates,
      }}
    >
      {children}
    </IntegrationsContext.Provider>
  );
};

export const isIntegrationActive = (
  integrationName: keyof IntegrationsState["integrations"]
): boolean => integrationStateManager.isIntegrationActive(integrationName);

export const getAllIntegrations = (): Integration[] =>
  integrationStateManager.getAllIntegrations();

export const getAllConnectionStates = (): IntegrationConnectionState[] =>
  integrationStateManager.getAllConnectionStates();
